import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import {
  HelperText,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
} from '@c2fo/liquidity';
import { PercentIcon } from '@c2fo/liquidity/icons';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { isValidRateInput } from '@/utils/validateRate';
import useOfferModalFormContext from '../useOfferModalFormContext';
import { validateRateInputForTakerMarket } from '../utils';

export const PriceDiscoveryRateInput = ({ takerMarket }: { takerMarket: TakerMarket }) => {
  const { t } = useTranslation();
  const { control, formState, watch } = useOfferModalFormContext();
  const { asNumber } = useLocaleFormat();

  const [touched, setTouched] = useState(false);

  const rateOptions = [
    { value: 'disc', label: t('core.discount'), desc: t('offer.flatRateMessage') },
    { value: 'apr', label: t('core.apr'), desc: t('offer.recurringMessage') },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 p-4">
      <div className="space-y-2">
        <Label htmlFor="rateStr">{t('core.offerRate')}</Label>
        <Controller
          control={control}
          name="rateStr"
          render={({ field }) => (
            <TextInput
              className="w-full"
              hasError={!!formState.errors.rateStr}
              id="rateStr"
              endIcon={PercentIcon}
              // Default existing rate value to user locale if exists, otherwise use entered value
              value={
                touched
                  ? field.value
                  : parseFloat(field.value)
                  ? asNumber({ value: parseFloat(field.value), decimals: 2 })
                  : ''
              }
              onChange={(e) => {
                if (isValidRateInput(e.target.value)) {
                  field.onChange(e.target.value);
                  setTouched(true);
                }
              }}
              placeholder={t('taker.setARate')}
            />
          )}
          rules={{
            validate: (rateInput) => {
              const { errorMessage } = validateRateInputForTakerMarket({
                rateInput,
                rateType: watch('rateTypeStr'),
                takerMarket,
                t,
              });

              return errorMessage;
            },
          }}
        />
        {formState.errors.rateStr?.message && (
          <HelperText variant="error">{t(formState.errors.rateStr.message)}</HelperText>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="rateTypeStr">{t('core.rateType')}</Label>
        <Controller
          control={control}
          name="rateTypeStr"
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value ?? ''}>
              <SelectTrigger aria-label={t('core.rateType')} hasError={!!formState.errors.rateTypeStr}>
                <SelectValue placeholder={t('offer.selectRateType')} />
              </SelectTrigger>
              <SelectContent>
                {rateOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <p>{option.label}</p>
                    <p className="block text-wrap text-sm [button_&]:hidden">{option.desc}</p>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          rules={{ required: true }}
        />
      </div>
    </div>
  );
};
